import type {
    ITenantServiceLicense,
    ITenantServiceProduct,
} from '../../interfaces/service';
import { getServiceFromProduct } from './ManageLicensesHelper';

export const customTenantAllocationSections = [ 'aiservices', 'testmanager', 'verticalsolutions', 'apps' ];

const productToCustomTenantAllocationSection: Record<string, string> = {
    AIU: 'aiservices',
    AGU: 'aiservices',
    HEAL: 'aiservices',
    HEALTEST: 'aiservices',
    SPR: 'aiservices',
    PLTU: 'aiservices',
    TEU: 'testmanager',
    PERFTEST: 'testmanager',
    MRSU: 'verticalsolutions',
    LU: 'verticalsolutions',
    APPU: 'apps',
    S2PU: 'verticalsolutions',
    RCMU: 'verticalsolutions',
    GIC: 'aiservices',
};

export const isTenantAllocatableConsumable = (consumableCode: string): boolean =>
    productToCustomTenantAllocationSection[consumableCode] !== undefined;

export const isProductUnavailableInAllocationSection = (product: ITenantServiceProduct): boolean => {
    if (window.env.unavailableProductCodes?.includes(product.code) ?? false) {
        return true;
    }

    return false;
};

export const mapServiceLicenseToTenantCustomAllocationSections = (serviceLicense: ITenantServiceLicense): ITenantServiceLicense[] => {
    if (serviceLicense.serviceType !== 'tenant') {
        return [ serviceLicense ];
    }

    const sections: ITenantServiceLicense[] = [];

    for (const product of serviceLicense.products) {
        const sectionName = productToCustomTenantAllocationSection[product.code];
        const idx = sections.findIndex(s => s.serviceType === sectionName);

        if (idx === -1) {
            sections.push({
                serviceType: sectionName,
                products: [ product ],
            });
        } else {
            sections[idx].products.push(product);
        }
    }

    return sections;
};

export const mapTenantAllocationSectionToServiceLicense = (allocationSection: ITenantServiceLicense): ITenantServiceLicense[] => {
    if (customTenantAllocationSections.find(section => section === allocationSection.serviceType) === undefined) {
        return [ allocationSection ];
    }

    // Group products by their actual service type
    const serviceMap = new Map<string, ITenantServiceProduct[]>();

    for (const product of allocationSection.products) {
        const actualService = getServiceFromProduct(product.code) ?? 'tenant';

        const products = serviceMap.get(actualService);
        if (products) {
            products.push(product);
        } else {
            serviceMap.set(actualService, [ product ]);
        }
    }

    // Return array of service licenses grouped by actual service type
    return Array.from(serviceMap.entries()).map(([ serviceType, products ]) => ({
        serviceType,
        products,
    }));
};

export const minMaxAllowedProductQty = (product: ITenantServiceProduct) => {
    const minQty = (product.reserved + product.used) || 0;

    if (product.unlimited) {
        return {
            minQuantity: minQty,
            maxQuantity: Number.MAX_SAFE_INTEGER,
        };
    }

    const maxQty = product.quantity + product.available > 0
        ? product.quantity + product.available
        : product.quantity;

    return {
        minQuantity: minQty,
        maxQuantity: Math.max(minQty, maxQty),
    };
};

export const moveProductsToCustomSection = (serviceLicenses: ITenantServiceLicense[]): ITenantServiceLicense[] => {
    if (!serviceLicenses || serviceLicenses.length === 0) {
        return serviceLicenses;
    }

    let tenantService = serviceLicenses.find(s => s.serviceType === 'tenant');

    if (!tenantService) {
        tenantService = {
            serviceType: 'tenant',
            products: [],
        };
        serviceLicenses.push(tenantService);
    }

    const productsToMove = Object.keys(productToCustomTenantAllocationSection);

    for (const service of serviceLicenses) {
        if (service.serviceType === 'tenant') {
            continue;
        }

        const productsToMigrate = service.products.filter(p =>
            productsToMove.includes(p.code) &&
            !tenantService.products.some(tp => tp.code === p.code),
        );

        if (productsToMigrate.length > 0) {
            tenantService.products.push(...productsToMigrate);
            service.products = service.products.filter(p =>
                !productsToMigrate.includes(p),
            );
        }
    }

    return serviceLicenses;
};
